import { useGlobalPref } from '#hooks/useGlobalPref';

import { aFecha, escribirAvisos, leerAvisos } from './avisos';
import type { AvisosGavi } from './avisos';

/**
 * Notificaciones de este dispositivo (pref global `avisosGavi`). Al guardar
 * «Cada 2 días» sin día de partida, cuenta desde hoy.
 */
export function useAvisos() {
  const [raw, guardar] = useGlobalPref('avisosGavi');
  const avisos = leerAvisos(raw);
  function setAvisos(c: AvisosGavi) {
    const siguiente =
      c.ritmo === 'dos' && !c.inicio ? { ...c, inicio: aFecha(new Date()) } : c;
    guardar(escribirAvisos(siguiente));
  }
  return { avisos, elegido: Boolean(raw), setAvisos };
}

export type PermisoAvisos =
  | 'concedido'
  | 'denegado'
  | 'pendiente'
  | 'sin-soporte';

/** Qué dejó decidido el móvil sobre las notificaciones de Rumbo. */
export function permisoAvisos(): PermisoAvisos {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'sin-soporte';
  }
  switch (Notification.permission) {
    case 'granted':
      return 'concedido';
    case 'denied':
      return 'denegado';
    default:
      return 'pendiente';
  }
}

/** Pide permiso (ventana del sistema). `true` si queda concedido. */
export async function pedirPermisoAvisos(): Promise<boolean> {
  const actual = permisoAvisos();
  if (actual !== 'pendiente') {
    return actual === 'concedido';
  }
  try {
    return (await Notification.requestPermission()) === 'granted';
  } catch {
    return false;
  }
}

/** Logo de Rumbo en color y brújula en silueta (la de la barra de Android). */
export const ICONO_AVISO = '/android-chrome-192x192.png';
export const INSIGNIA_AVISO = '/insignia-aviso.png';

/**
 * Una notificación ahora mismo (la de prueba). Mejor por el service worker:
 * en Android, `new Notification()` no funciona dentro de una app.
 */
export async function mostrarAviso(titulo: string, cuerpo: string) {
  if (!(await pedirPermisoAvisos())) {
    return false;
  }
  const opciones: NotificationOptions = {
    body: cuerpo,
    icon: ICONO_AVISO,
    badge: INSIGNIA_AVISO,
    tag: 'rumbo-prueba',
  };
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    if (reg) {
      await reg.showNotification(titulo, opciones);
      return true;
    }
    new Notification(titulo, opciones);
    return true;
  } catch {
    return false;
  }
}
